import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDate, formatDuration, formatTime } from '../../utils/formatters';
import api from '../../services/api';
import StatusBadge from '../../components/StatusBadge';
import { confirmDialog, promptDialog, successAlert, errorAlert } from '../../utils/alerts';

const ApprovalList = () => {
  const [entries, setEntries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkNotes, setBulkNotes] = useState('');
  const [filters, setFilters] = useState({
    consultant: '',
    approval_status: 'pending',
    project: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchConsultants();
    fetchProjects();
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchConsultants = async () => {
    try {
      const response = await api.get('/users/?role=consultor');
      setConsultants(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar consultores', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects/');
      setProjects(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar projetos', err);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (filters.consultant) params.append('consultant', filters.consultant);
      if (filters.approval_status) params.append('approval_status', filters.approval_status);
      if (filters.project) params.append('project', filters.project);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get(`/time-entries/?${params.toString()}`);
      const data = response.data.results || response.data;
      setEntries(data);
      setSelected([]);
    } catch (err) {
      setError('Erro ao carregar lançamentos.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === entries.length) {
      setSelected([]);
    } else {
      setSelected(entries.map((e) => e.id));
    }
  };

  const handleApprove = async (id) => {
    // Confirmação antes de aprovar
    const confirmed = await confirmDialog({
      title: 'Aprovar lançamento',
      text: 'Deseja aprovar este lançamento?',
      icon: 'question',
      confirmText: 'Aprovar',
    });
    if (!confirmed) return;

    try {
      await api.post(`/time-entries/${id}/approve/`);
      await successAlert('Aprovado!', 'Lançamento aprovado com sucesso.');
      fetchEntries();
    } catch (err) {
      errorAlert('Erro ao aprovar', 'Não foi possível aprovar o lançamento.');
    }
  };

  const handleReject = async (id) => {
    // Confirmação antes de recusar
    const confirmed = await confirmDialog({
      title: 'Recusar lançamento',
      text: 'Deseja recusar este lançamento?',
      icon: 'warning',
      confirmText: 'Recusar',
    });
    if (!confirmed) return;

    try {
      await api.post(`/time-entries/${id}/reject/`);
      await successAlert('Recusado!', 'Lançamento recusado com sucesso.');
      fetchEntries();
    } catch (err) {
      errorAlert('Erro ao recusar', 'Não foi possível recusar o lançamento.');
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.length === 0) return;

    try {
      await api.post('/time-entries/bulk-approve/', {
        ids: selected,
        action,
        manager_notes: bulkNotes,
      });
      toast.success(`Lançamentos ${action === 'approve' ? 'aprovados' : 'recusados'} com sucesso!`);
      setBulkNotes('');
      fetchEntries();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro na ação em lote.';
      toast.error(msg);
    }
  };

  const openNoteModal = async (entry) => {
    // Observação do gestor via SweetAlert2 (substitui window.prompt)
    const notes = await promptDialog({
      title: 'Observação do gestor',
      text: 'Informe a observação para este lançamento:',
      placeholder: 'Digite a observação...',
      inputValue: entry.manager_notes || '',
    });
    if (notes === null) return;
    try {
      await api.post(`/time-entries/${entry.id}/manager_note/`, { manager_notes: notes });
      await successAlert('Salvo!', 'Observação salva com sucesso.');
      fetchEntries();
    } catch (err) {
      errorAlert('Erro ao salvar', 'Não foi possível salvar a observação.');
    }
  };

  const allFromSameConsultant = () => {
    if (selected.length === 0) return false;
    const selectedEntries = entries.filter((e) => selected.includes(e.id));
    const consultantId = selectedEntries[0].consultant;
    return selectedEntries.every((e) => e.consultant === consultantId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <select
            name="consultant"
            value={filters.consultant}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Filtrar por consultor</option>
            {consultants.map((c) => (
              <option key={c.id} value={c.id}>{c.first_name || c.username}</option>
            ))}
          </select>

          <select
            name="approval_status"
            value={filters.approval_status}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Todos os status</option>
            <option value="pending">Pendente</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Recusado</option>
          </select>

          <select
            name="project"
            value={filters.project}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Todos os projetos</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <input
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="De"
          />

          <input
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="Até"
          />
        </div>
      </div>

      {selected.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="text-sm text-blue-900 font-medium">
            {selected.length} selecionado(s)
          </span>
          <input
            type="text"
            placeholder="Observação para todos (opcional)"
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            className="flex-1 px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkAction('approve')}
              disabled={!allFromSameConsultant()}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              <CheckCircle size={16} />
              Aprovar
            </button>
            <button
              onClick={() => handleBulkAction('reject')}
              disabled={!allFromSameConsultant()}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              <XCircle size={16} />
              Recusar
            </button>
          </div>
        </div>
      )}

      {!allFromSameConsultant() && selected.length > 0 && (
        <div className="text-sm text-red-600">
          Ações em lote devem ser de um único consultor.
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={entries.length > 0 && selected.length === entries.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Consultor</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente/Projeto</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Períodos</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400 text-sm">
                    Nenhum lançamento encontrado.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(entry.id)}
                        onChange={() => toggleSelect(entry.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 text-gray-900">{entry.consultant_name}</td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(entry.entry_date)}</td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="font-medium text-gray-900">{entry.client_name}</div>
                      <div className="text-xs text-gray-500">{entry.project_name}</div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {entry.periods?.map((p, i) => (
                        <div key={i} className="text-xs">
                          {formatTime(p.start_time)} - {formatTime(p.end_time)}
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4 text-gray-900 font-medium">
                      {formatDuration(entry.total_hours)}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={entry.approval_status} type="approval" />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {entry.approval_status !== 'approved' && (
                          <button
                            onClick={() => handleApprove(entry.id)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Aprovar"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        {entry.approval_status !== 'rejected' && (
                          <button
                            onClick={() => handleReject(entry.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Recusar"
                          >
                            <XCircle size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => openNoteModal(entry)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Observação"
                        >
                          <MessageSquare size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApprovalList;
